import { create } from 'zustand';
import { useMemo } from 'react';
import { httpClient } from '@/shared/lib/http-client';
import { API_ENDPOINTS, AUTH_TOKEN_KEY } from '@/shared/config/api';
import type {
  AuthState,
  AuthActions,
  LoginCredentials,
  LoginResponse,
  VerifyTokenResponse
} from '@/shared/types';

type AuthStore = AuthState & AuthActions;

const getStoredToken = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

const clearStoredToken = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

export const useAuthStore = create<AuthStore>()((set, get) => {
  const initialToken = getStoredToken();

  if (initialToken) {
    httpClient.setAuthToken(initialToken);
  }

  httpClient.setTokenExpirationHandler(() => {
    get().handleTokenExpiration();
  });

  return {
    isAuthenticated: false,
    user: null,
    token: initialToken,
    isLoading: false,
    error: null,

  login: async (credentials: LoginCredentials): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      const response = await httpClient.post<LoginResponse>(
        API_ENDPOINTS.AUTH.LOGIN,
        credentials
      );

      const { access_token, user } = response.data;

      httpClient.setAuthToken(access_token);

      set({
        isAuthenticated: true,
        user,
        token: access_token,
        isLoading: false,
        error: null
      });
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { message?: string; error?: string } } })
          ?.response?.data?.message ||
        (error as { response?: { data?: { message?: string; error?: string } } })
          ?.response?.data?.error ||
        'Ошибка входа в систему';

      set({
        isAuthenticated: false,
        user: null,
        token: null,
        isLoading: false,
        error: errorMessage
      });

      throw new Error(errorMessage);
    }
  },

  logout: (): void => {
    clearStoredToken();
    httpClient.setAuthToken('');

    set({
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false,
      error: null
    });

    httpClient.post(API_ENDPOINTS.AUTH.LOGOUT).catch(() => {});
  },

  verifyToken: async (): Promise<boolean> => {
    const { token } = get();

    if (!token) {
      get().logout();
      return false;
    }

    set({ isLoading: true });

    try {
      const response = await httpClient.get<VerifyTokenResponse>(
        API_ENDPOINTS.AUTH.VERIFY
      );

      const { user } = response.data;

      set({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null
      });

      return true;
    } catch {
      get().logout();
      return false;
    }
  },

  clearError: (): void => {
    set({ error: null });
  },

  setLoading: (loading: boolean): void => {
    set({ isLoading: loading });
  },

    handleTokenExpiration: (): void => {
      get().logout();
    }
  };
});

export const useAuthState = (): AuthState => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);

  return useMemo(
    () => ({
      isAuthenticated,
      user,
      token,
      isLoading,
      error
    }),
    [isAuthenticated, user, token, isLoading, error]
  );
};

export const useAuthActions = (): AuthActions & { handleTokenExpiration: () => void } => {
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const verifyToken = useAuthStore((state) => state.verifyToken);
  const clearError = useAuthStore((state) => state.clearError);
  const setLoading = useAuthStore((state) => state.setLoading);
  const handleTokenExpiration = useAuthStore((state) => state.handleTokenExpiration);

  return useMemo(
    () => ({
      login,
      logout,
      verifyToken,
      clearError,
      setLoading,
      handleTokenExpiration
    }),
    [login, logout, verifyToken, clearError, setLoading, handleTokenExpiration]
  );
};
